import { create } from 'zustand';
import { PlanDetails, PlanType, Address, CurrencyCode, Currency, ServiceType } from '../types/api';
import { AddressWithId } from '../services/addressService';

// Types
type SubscriptionType = 'monthly' | 'yearly' | 'trial';
type SubscriptionLevel = 'standard' | 'premium';
type PaymentType = 'one_time' | 'subscription';
export type PaymentMethod = 'card' | 'promptpay' | 'qr' | 'crypto';
type PromotionDuration = 'once' | 'forever' | 'repeating';

interface SubscriptionState {
  // Subscription options
  subscriptionType: SubscriptionType;
  subscriptionLevel: SubscriptionLevel;
  paymentMethod: PaymentMethod;
  paymentType: PaymentType;
  serviceType: ServiceType;
  
  // UI state
  loading: boolean;
  error: string | null;
  showPromptPayInfo: boolean;
  showAddressModal: boolean;
  hasAttemptedAddressSelection: boolean;
  showManualAddressPrompt: boolean;
  
  // Plan details
  planDetails: PlanDetails | null;
  
  // Gift state
  isGift: boolean;
  recipientName: string;
  recipientPhone: string;
  giftMessage: string;
  senderName: string;
  senderPhone: string;
  
  // Schedule state
  selectedTimeSlots: string[];
  selectedDate: string;
  weeklyDay: number;
  
  // Preferences state
  preferences: Record<string, boolean>;
  
  // Promotion state
  promotionCode: string | null;
  promotionDetails: {
    isLoaded: boolean;
    isValid: boolean;
    percentOff: number;
    amountOff: number;
    couponId: string;
    description: string;
    allowedPaymentTypes: PaymentType[];
    duration: PromotionDuration;
    durationInMonths: number | null;
    minimumAmount: number;
    currency: string | null;
    errorMessage?: string;
    error?: string;
  };
  
  // Flags
  hasShownScheduleToast: boolean;
  hasShownPromoToast: boolean;
  hasShownPaymentSwitchToast: boolean;
  
  // Currency
  currency: CurrencyCode;
  availableCurrencies: Currency[];
  
  // Actions
  setSubscriptionType: (type: SubscriptionType) => void;
  setSubscriptionLevel: (level: SubscriptionLevel) => void;
  setPaymentMethod: (method: PaymentMethod) => void;
  setPaymentType: (type: PaymentType) => void;
  setServiceType: (type: ServiceType) => void;
  updateSubscriptionState: (updates: Partial<{
    type: SubscriptionType;
    level: SubscriptionLevel;
    paymentMethod: PaymentMethod;
    paymentType: PaymentType;
    serviceType: ServiceType;
  }>) => void;
  
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setShowPromptPayInfo: (show: boolean) => void;
  setShowAddressModal: (show: boolean) => void;
  setHasAttemptedAddressSelection: (attempted: boolean) => void;
  setShowManualAddressPrompt: (show: boolean) => void;
  
  setPlanDetails: (details: PlanDetails | null) => void;
  
  updateGiftState: (updates: Partial<{
    recipientName: string;
    recipientPhone: string;
    giftMessage: string;
    senderName: string;
    senderPhone: string;
  }>) => void;
  
  updateScheduleState: (updates: Partial<{
    selectedTimeSlots: string[];
    selectedDate: string;
    weeklyDay: number;
  }>) => void;
  
  updatePreferences: (preferences: Record<string, boolean>) => void;
  
  setPromotionCode: (code: string | null) => void;
  setPromotionDetails: (details: Partial<SubscriptionState['promotionDetails']>) => void;
  
  setHasShownScheduleToast: (shown: boolean) => void;
  setHasShownPromoToast: (shown: boolean) => void;
  setHasShownPaymentSwitchToast: (shown: boolean) => void;
  
  reset: () => void;
  
  setCurrency: (currency: CurrencyCode) => void;
}

// Default values
const DEFAULT_TIME_SLOTS = ['9:00 AM - 12:00 PM', '2:00 PM - 5:00 PM'];
const DEFAULT_WEEKLY_DAY = 1;

// Create store
export const useSubscriptionStore = create<SubscriptionState>((set) => ({
  // Default state
  subscriptionType: 'monthly',
  subscriptionLevel: 'standard',
  paymentMethod: 'card',
  paymentType: 'subscription',
  serviceType: 'flower',
  
  loading: false,
  error: null,
  showPromptPayInfo: false,
  showAddressModal: false,
  hasAttemptedAddressSelection: false,
  showManualAddressPrompt: false,
  
  planDetails: null,
  
  isGift: false,
  recipientName: '',
  recipientPhone: '',
  giftMessage: '',
  senderName: '',
  senderPhone: '',
  
  selectedTimeSlots: DEFAULT_TIME_SLOTS,
  selectedDate: '',
  weeklyDay: DEFAULT_WEEKLY_DAY,
  
  // Initialize empty preferences
  preferences: {},
  
  promotionCode: null,
  promotionDetails: {
    isLoaded: false,
    isValid: false,
    percentOff: 0,
    amountOff: 0,
    couponId: '',
    description: '',
    allowedPaymentTypes: [],
    duration: 'once',
    durationInMonths: null,
    minimumAmount: 0,
    currency: null
  },
  
  hasShownScheduleToast: false,
  hasShownPromoToast: false,
  hasShownPaymentSwitchToast: false,
  
  // Currency
  currency: 'thb',
  availableCurrencies: [
    { code: 'thb', symbol: '฿', name: 'Thai Baht', default: true },
    { code: 'usd', symbol: '$', name: 'US Dollar', default: false }
  ],
  
  // Actions
  setSubscriptionType: (type) => set({ subscriptionType: type }),
  setSubscriptionLevel: (level) => set({ subscriptionLevel: level }),
  setPaymentMethod: (method) => set({ paymentMethod: method }),
  setPaymentType: (type) => set({ paymentType: type }),
  setServiceType: (type) => set({ serviceType: type }),
  updateSubscriptionState: (updates) => set((state) => ({
    subscriptionType: updates.type || state.subscriptionType,
    subscriptionLevel: updates.level || state.subscriptionLevel,
    paymentMethod: updates.paymentMethod || state.paymentMethod,
    paymentType: updates.paymentType || state.paymentType,
    serviceType: updates.serviceType || state.serviceType
  })),
  
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setShowPromptPayInfo: (show) => set({ showPromptPayInfo: show }),
  setShowAddressModal: (show) => set({ showAddressModal: show }),
  setHasAttemptedAddressSelection: (attempted) => set({ hasAttemptedAddressSelection: attempted }),
  setShowManualAddressPrompt: (show) => set({ showManualAddressPrompt: show }),
  
  setPlanDetails: (details) => set({ planDetails: details }),
  
  updateGiftState: (updates) => set((state) => ({
    recipientName: updates.recipientName !== undefined ? updates.recipientName : state.recipientName,
    recipientPhone: updates.recipientPhone !== undefined ? updates.recipientPhone : state.recipientPhone,
    giftMessage: updates.giftMessage !== undefined ? updates.giftMessage : state.giftMessage,
    senderName: updates.senderName !== undefined ? updates.senderName : state.senderName,
    senderPhone: updates.senderPhone !== undefined ? updates.senderPhone : state.senderPhone
  })),
  
  updateScheduleState: (updates) => set((state) => ({
    selectedTimeSlots: updates.selectedTimeSlots || state.selectedTimeSlots,
    selectedDate: updates.selectedDate || state.selectedDate,
    weeklyDay: updates.weeklyDay !== undefined ? updates.weeklyDay : state.weeklyDay
  })),
  
  // Preferences update function
  updatePreferences: (preferences) => set({ preferences }),
  
  setPromotionCode: (code) => set({ promotionCode: code }),
  setPromotionDetails: (details) => set((state) => ({
    promotionDetails: {
      ...state.promotionDetails,
      ...details
    }
  })),
  
  setHasShownScheduleToast: (shown) => set({ hasShownScheduleToast: shown }),
  setHasShownPromoToast: (shown) => set({ hasShownPromoToast: shown }),
  setHasShownPaymentSwitchToast: (shown) => set({ hasShownPaymentSwitchToast: shown }),
  
  reset: () => set({
    subscriptionType: 'monthly',
    subscriptionLevel: 'standard',
    paymentMethod: 'card',
    paymentType: 'subscription',
    serviceType: 'flower',
    
    loading: false,
    error: null,
    showPromptPayInfo: false,
    showAddressModal: false,
    hasAttemptedAddressSelection: false,
    
    planDetails: null,
    
    recipientName: '',
    recipientPhone: '',
    giftMessage: '',
    senderName: '',
    senderPhone: '',
    
    selectedTimeSlots: DEFAULT_TIME_SLOTS,
    selectedDate: '',
    weeklyDay: DEFAULT_WEEKLY_DAY,
    
    // Reset preferences
    preferences: {},
    
    promotionDetails: {
      isLoaded: false,
      isValid: false,
      percentOff: 0,
      amountOff: 0,
      couponId: '',
      description: '',
      allowedPaymentTypes: [],
      duration: 'once',
      durationInMonths: null,
      minimumAmount: 0,
      currency: null
    },
    
    hasShownScheduleToast: false,
    hasShownPromoToast: false,
    hasShownPaymentSwitchToast: false
  }),
  
  setCurrency: (currency) => set({ currency })
})); 